import { useCallback, useState } from "react";
import { API_BASE } from "../../lib/api";
import useCopilotSessions from "./useCopilotSessions";
import { streamCopilotChat } from "./copilotStream";

export const mkMsg = (role, content) => ({
  id: `m-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  role,
  content,
});

const WELCOME = mkMsg(
  "assistant",
  "Hi — I'm SmartERP Copilot. Ask me anything about finance, HR, or supply chain data."
);

/**
 * Central Copilot chat hook: composes session management (sidebar/history)
 * and SSE streaming into a single API that the UI can consume.
 */
export default function useCopilotChat() {
  const [messages, setMessages] = useState([WELCOME]);
  const [streaming, setStreaming] = useState(false);

  const onNewMessages = useCallback((raw) => {
    const msgs = (raw || []).map((m) => mkMsg(m.role, m.content));
    setMessages(msgs.length ? msgs : [WELCOME]);
  }, []);

  const {
    sessions, activeSessionId, sessionRef,
    setActive, clearActive, loadSession, deleteSession, refreshSessions,
  } = useCopilotSessions({ onNewMessages });

  const startNewChat = useCallback(() => {
    clearActive();
    setMessages([WELCOME]);
  }, [clearActive]);

  const appendToLast = useCallback((chunk) => {
    setMessages((m) => {
      const next = [...m];
      const last = next[next.length - 1];
      next[next.length - 1] = { ...last, role: "assistant", content: (last.content || "") + chunk };
      return next;
    });
  }, []);

  const replaceLast = useCallback((content) => {
    setMessages((m) => {
      const next = [...m];
      const last = next[next.length - 1];
      next[next.length - 1] = { ...last, role: "assistant", content };
      return next;
    });
  }, []);

  const send = useCallback(async (text) => {
    const q = (text ?? "").trim();
    if (!q || streaming) return;
    setMessages((m) => [...m, mkMsg("user", q), mkMsg("assistant", "")]);
    setStreaming(true);
    try {
      const result = await streamCopilotChat({
        apiBase: API_BASE,
        message: q,
        sessionId: sessionRef.current,
        onSession: (sid) => { setActive(sid); refreshSessions(); },
        onChunk: appendToLast,
      });
      if (!result.ok) replaceLast(result.error);
    } catch (e) {
      replaceLast(`Connection error: ${e.message}`);
    } finally {
      setStreaming(false);
      refreshSessions();
    }
  }, [streaming, sessionRef, setActive, refreshSessions, appendToLast, replaceLast]);

  return {
    messages,
    streaming,
    sessions,
    activeSessionId,
    startNewChat,
    loadSession,
    deleteSession,
    send,
  };
}
